/**
 * Permissions service - fetches current user's allowed roles / privileges.
 */

import type { UserRole } from "../constants/permissions";
import { apiClient } from "./apiClient";

/**
 * Interface for privileges response (GET /authz/privileges)
 */
export interface UserPrivilegesData {
  privileges: string[];
}

/** Super-user privilege — satisfies any UI privilege check. */
export const AIHUB_ADMIN_PRIVILEGE = "aihub.admin";

/**
 * Privilege codes used by Classification AI Plan / AI Hub (for reference / docs).
 * Runtime gating uses the list returned by {@link getCurrentUserPrivileges}.
 */
export const ALL_PRIVILEGES: string[] = [
  AIHUB_ADMIN_PRIVILEGE,
  "aihub.auto_tagging.export",
  "aihub.auto_tagging.manage",
  "aihub.auto_tagging.read",
  "aihub.conditional_tagging.manage",
  "aihub.conditional_tagging.read",
  "aihub.object_to_object.manage",
  "aihub.object_to_object.read",
  "aihub.assessment.read",
  "aihub.object_to_object_assessment.execute",
  "aihub.object_to_object_assessment.read",
  "aihub.tagging_result.manage",
  "aihub.tagging_result.read",
];

/** Auto-tagging plan / triage privilege codes used for UI gating. */
export const AUTO_TAGGING_PRIVILEGES = {
  read: "aihub.auto_tagging.read",
  manage: "aihub.auto_tagging.manage",
  export: "aihub.auto_tagging.export",
} as const;

/**
 * Conditional (Enhanced Tagging Rule) plan privilege codes.
 * These routes no longer accept aihub.auto_tagging.* — UI checks must use this set.
 */
export const CONDITIONAL_TAGGING_PRIVILEGES = {
  read: "aihub.conditional_tagging.read",
  manage: "aihub.conditional_tagging.manage",
} as const;

/** Object-to-Object plan privilege codes used for UI gating. */
export const OBJECT_TO_OBJECT_PRIVILEGES = {
  read: "aihub.object_to_object.read",
  manage: "aihub.object_to_object.manage",
} as const;

/**
 * Classification / Conditional Classification / Triage Result privilege codes
 * (AI Hub Result tabs).
 */
export const TAGGING_RESULT_PRIVILEGES = {
  read: "aihub.tagging_result.read",
  manage: "aihub.tagging_result.manage",
} as const;

/** Object-to-Object assessment Result privilege codes (AI Hub Result tab). */
export const OBJECT_TO_OBJECT_ASSESSMENT_PRIVILEGES = {
  read: "aihub.object_to_object_assessment.read",
  execute: "aihub.object_to_object_assessment.execute",
} as const;

/** Implementation record / assessment pack privilege codes (AI Hub Result tab + pack deep link). */
export const IMPLEMENTATION_RECORD_PRIVILEGES = {
  read: "aihub.assessment.read",
} as const;

/** Any privilege that grants access to the Classification AI Plan page. */
export const AI_PLAN_PAGE_READ_PRIVILEGES = [
  AUTO_TAGGING_PRIVILEGES.read,
  CONDITIONAL_TAGGING_PRIVILEGES.read,
  OBJECT_TO_OBJECT_PRIVILEGES.read,
] as const;

/** Any privilege that grants access to the AI Hub (Result) page. */
export const AI_HUB_PAGE_READ_PRIVILEGES = [
  TAGGING_RESULT_PRIVILEGES.read,
  OBJECT_TO_OBJECT_ASSESSMENT_PRIVILEGES.read,
  IMPLEMENTATION_RECORD_PRIVILEGES.read,
] as const;

/**
 * Mock: Returns the permissions (allowed roles) for the current user.
 * Each permission maps to a role the user can assume.
 */
export async function getCurrentUserPermissions(): Promise<UserRole[]> {
  await new Promise((resolve) => setTimeout(resolve, 0));
  return [
    "admin",
    "triage_officer",
    "department_assessor",
    "department_approver",
  ] as UserRole[];
}

/**
 * Fetches current user's privileges from the backend.
 * Always returns the API list (or [] on failure / empty) — never a hardcoded full set.
 */
export async function getCurrentUserPrivileges(): Promise<string[]> {
  try {
    const result = await apiClient.get<UserPrivilegesData>("/authz/privileges");
    if (result.status === "success" && Array.isArray(result.data?.privileges)) {
      return result.data.privileges;
    }
    return [];
  } catch (error) {
    console.error("Failed to fetch user privileges:", error);
    return [];
  }
}
